// API response types — kept in lockstep with server/src/api/*.ts.
// (When the app grows, lift these into a shared workspace.)

export interface Family {
  id: number;
  slug: string;
  name: string;
  sortOrder: number;
  layoutCount: number;
}

/** Per-render URLs: small thumbnail for cards/grids, full-res original for the lightbox. */
export interface RenderUrls {
  thumbUrl: string | null;
  fullUrl: string | null;
}

export interface LayoutListItem {
  id: number;
  code: string;
  familySlug: string;
  familyName: string;
  widthInches: number | null;
  depthInches: number | null;
  totalPriceCents: number;
  itemCount: number;
  sortOrder: number;
  /** Best thumbnail for the gallery card (first environmental → master → null when no renders yet). */
  heroUrl: string | null;
  /** Delivered renders keyed by wire key (master|environmental-1..5|dimensional). Empty when none. */
  renders: Record<string, RenderUrls>;
}

export interface LayoutDetailItem {
  sku: string;
  description: string;
  quantity: number;
  unitPriceCents: number;
  lineTotalCents: number;
  sortOrder: number;
}

export interface LayoutDetail {
  id: number;
  code: string;
  family: { id: number; slug: string; name: string };
  widthInches: number | null;
  depthInches: number | null;
  totalPriceCents: number;
  items: LayoutDetailItem[];
  /** Renders keyed by wire key ('master' | 'environmental-1'..'environmental-5' | 'dimensional').
   * Keys without a delivered asset are absent (client falls back to placeholder). */
  renders: Record<string, RenderUrls>;
}

import { authHeaders } from './auth/headers';

async function get<T>(path: string): Promise<T> {
  const auth = await authHeaders();
  const res = await fetch(path, {
    headers: { Accept: 'application/json', ...auth },
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`GET ${path} failed: ${res.status} ${text}`);
  }
  return (await res.json()) as T;
}

// ----- Me / Role -----

export interface MeResponse {
  oid: string;
  email?: string;
  name?: string;
  role: 'user' | 'design' | 'admin';
}

export function fetchMe(): Promise<MeResponse> {
  return get('/api/me');
}

// ----- Catalog -----

export function fetchFamilies(): Promise<{ families: Family[] }> {
  return get('/api/families');
}

export function fetchLayouts(familySlug?: string, productId?: number): Promise<{ layouts: LayoutListItem[] }> {
  const parts: string[] = [];
  if (familySlug) parts.push(`family=${encodeURIComponent(familySlug)}`);
  if (productId !== undefined) parts.push(`productId=${productId}`);
  const qs = parts.length ? `?${parts.join('&')}` : '';
  return get(`/api/layouts${qs}`);
}

// ----- Products -----

export interface ProductItem {
  id: number;
  sku: string;
  name: string;
}

export function fetchProducts(): Promise<{ products: ProductItem[] }> {
  return get('/api/products');
}

export function fetchLayout(code: string): Promise<{ layout: LayoutDetail }> {
  return get(`/api/layouts/${encodeURIComponent(code)}`);
}

// ----- Shares -----

export interface ShareSnapshotItem {
  layoutCode: string;
  familyName: string;
  familySlug: string;
  totalPriceCents: number;
  itemCount: number;
  widthInches: number | null;
  depthInches: number | null;
  includePricing: boolean;
  selectedRenders: string[];
}

export interface CreateShareRequest {
  items: ShareSnapshotItem[];
  name?: string;
  customerName?: string;
}

export interface CreateShareResponse {
  token: string;
  expiresAt: string;
}

export interface SharedDeck {
  items: ShareSnapshotItem[];
  name: string;
  customerName: string;
  createdAt: string;
  expiresAt: string | null;
}

export async function createShare(body: CreateShareRequest): Promise<CreateShareResponse> {
  const auth = await authHeaders();
  const res = await fetch('/api/shares', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...auth,
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Could not create share link: ${res.status} ${text}`);
  }
  return (await res.json()) as CreateShareResponse;
}

// ----- Exports (PDF) -----

export interface DeckPdfExport {
  mode: 'deck';
  items: ShareSnapshotItem[];
  customerName?: string;
  deckName?: string;
}

export interface CatalogPdfExport {
  mode: 'catalog';
  includePricing: boolean;
}

export type PdfExportRequest = DeckPdfExport | CatalogPdfExport;

/** POSTs the export payload and triggers a browser download of the resulting PDF. */
export async function exportPdf(body: PdfExportRequest): Promise<void> {
  const auth = await authHeaders();
  const res = await fetch('/api/exports/pdf', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/pdf',
      ...auth,
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Export failed: ${res.status} ${text}`);
  }
  const blob = await res.blob();
  const filename =
    res.headers
      .get('content-disposition')
      ?.match(/filename="([^"]+)"/)?.[1] ?? 'idea-starter.pdf';
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Defer revoke so the browser has time to start the download
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Downloads the original source PDF (April 2026 Idea Starter document). */
export async function downloadOriginalPdf(): Promise<void> {
  const auth = await authHeaders();
  const res = await fetch('/api/exports/original', {
    headers: { Accept: 'application/pdf', ...auth },
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Download failed: ${res.status} ${text}`);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'IDEA STARTER - April 2026.pdf';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export async function fetchShare(token: string): Promise<{ share: SharedDeck }> {
  const res = await fetch(`/api/shares/${encodeURIComponent(token)}`, {
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) {
    if (res.status === 404) throw new Error('This link doesn’t exist.');
    if (res.status === 410) {
      const body = (await res.json().catch(() => null)) as { error?: string } | null;
      throw new Error(body?.error ?? 'This link is no longer available.');
    }
    throw new Error(`Could not load share: ${res.status}`);
  }
  return (await res.json()) as { share: SharedDeck };
}
